/**
 * TickDerived: what several systems work out from the state each tick, worked
 * out once and shared. `tick()` makes one per tick (`withTickDerived`); a
 * system asks for it with `tickDerived` once per run (outside `tick()`, e.g. a
 * test calling a system directly, it gets a fresh layer of its own, so nothing
 * is shared).
 *
 * Two lifetimes:
 * - Until invalidated (`invalidate`, via derivedRegistry.ts
 *   `invalidateDerived`): `vision` per player, `owners` (territory) and
 *   `buildingIndex`. These read where units stand, which units and buildings
 *   exist and their levels, never paths or orders, so they hold across the
 *   systems that only plan (automation, guard, pursuit, formation). Whatever
 *   moves units, deploys them, kills them or adds, finishes or grows
 *   buildings invalidates the layer.
 *   A system that moves units only by turning a step around (redirect.ts)
 *   drops it when it ends instead (`invalidateAfterSystem`).
 * - One system run (`endSystem`, after every system in `tick()`):
 *   `occupancy` and `blockers`. They read paths, which every planning system
 *   changes, so each system gets them fresh, worked out when it first asks,
 *   exactly as when each system built its own. A system may update its
 *   occupancy as it plans (occupancy.ts `forget`/`addHeading`).
 *
 * Pure caching: every value equals a fresh computation at the moment it's
 * first asked for, so the sim's results don't change.
 */
import type { HexTileMapKey } from '../hexmap/model/types';
import { activeDerived, registerDerived, unregisterDerived } from './derivedRegistry';
import { buildingIndex } from './footprint';
import { blockersFor, type Occupancy, occupancyOf } from './occupancy';
import { territoryOwners } from './territory';
import type { Building, EntityId, PlayerId, SimContext, SimState } from './types';
import { computeVision } from './vision';

interface TickDerived {
  /** What `player` sees right now (vision.ts `computeVision`). */
  vision(player: PlayerId): ReadonlySet<HexTileMapKey>;
  /** Tile → city holding it (territory.ts `territoryOwners`). */
  owners(): ReadonlyMap<HexTileMapKey, EntityId>;
  /** Tile → building covering it (footprint.ts `buildingIndex`). */
  buildingIndex(): ReadonlyMap<HexTileMapKey, Building>;
  /** Who stands, heads or is inside where (all-seeing), for this system run. */
  occupancy(): Occupancy;
  /** The blockers `player`'s paths avoid, as they see them (occupancy.ts `blockersFor`), for this system run. */
  blockers(player: PlayerId): ReadonlySet<HexTileMapKey>;
  /** Drops everything: units moved, appeared or died, or buildings changed. */
  invalidate(): void;
  /**
   * Drops everything once the running system ends: units moved (redirect.ts
   * turning a step around) in a system that may still read what it worked out
   * before, as it would working everything out alone.
   */
  invalidateAfterSystem(): void;
  /** Drops what reads paths (`occupancy`, `blockers`): called after each system. */
  endSystem(): void;
}

function createTickDerived(state: SimState, ctx: SimContext): TickDerived {
  const visions = new Map<PlayerId, Set<HexTileMapKey>>();
  const blockers = new Map<PlayerId, Set<HexTileMapKey>>();
  let owners: ReadonlyMap<HexTileMapKey, EntityId> | null = null;
  let index: Map<HexTileMapKey, Building> | null = null;
  let occupancy: Occupancy | null = null;
  let stale = false;
  const vision = (player: PlayerId) => {
    let set = visions.get(player);
    if (!set) {
      set = computeVision(state, ctx.rules, player);
      visions.set(player, set);
    }
    return set;
  };
  const drop = () => {
    visions.clear();
    owners = null;
    index = null;
  };
  const endSystem = () => {
    occupancy = null;
    blockers.clear();
    if (stale) drop();
    stale = false;
  };
  return {
    vision,
    owners: () => {
      owners ??= territoryOwners(state, ctx.rules);
      return owners;
    },
    buildingIndex: () => {
      index ??= buildingIndex(state);
      return index;
    },
    occupancy: () => {
      occupancy ??= occupancyOf(state, ctx.rules);
      return occupancy;
    },
    blockers: (player) => {
      let set = blockers.get(player);
      if (!set) {
        set = blockersFor(state, ctx.rules, player, vision(player));
        blockers.set(player, set);
      }
      return set;
    },
    invalidate: () => {
      drop();
      endSystem();
    },
    invalidateAfterSystem: () => {
      stale = true;
    },
    endSystem,
  };
}

/** The running tick's layer for `state`, or (outside `tick()`) a fresh one. Ask once per system run. */
function tickDerived(state: SimState, ctx: SimContext): TickDerived {
  return activeDerived(state) ?? createTickDerived(state, ctx);
}

/** Runs `run` with a fresh layer registered for `state` (what `tick()` does around its systems). */
function withTickDerived<T>(state: SimState, ctx: SimContext, run: (derived: TickDerived) => T): T {
  const derived = createTickDerived(state, ctx);
  registerDerived(state, derived);
  try {
    return run(derived);
  } finally {
    unregisterDerived(state);
  }
}

export type { TickDerived };
export { tickDerived, withTickDerived };
